import React from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { LoadingSpinner } from '../LoadingSpinner';
import { UI_Z_CLASS } from '../../utils/uiZIndex';
import type { RecurrenceScope } from '../../utils/activityRecurrence';

type RecurrenceScopeDialogProps = {
  mode: 'save' | 'delete';
  seriesLabel: string;
  onPick: (scope: RecurrenceScope) => void | Promise<void>;
  onCancel: () => void;
};

const OPTIONS: { scope: RecurrenceScope; title: string; hint: string }[] = [
  { scope: 'this', title: 'Nur dieser Termin', hint: 'Nur den ausgewählten Tag ändern.' },
  { scope: 'following', title: 'Dieser und folgende', hint: 'Ab diesem Tag eine neue Serie beginnen.' },
  { scope: 'series', title: 'Alle Termine', hint: 'Die gesamte Serie anpassen.' },
];

const DELETE_HINTS: Record<RecurrenceScope, string> = {
  this: 'Nur den ausgewählten Tag löschen.',
  following: 'Ab diesem Tag alle folgenden Termine löschen.',
  series: 'Die gesamte Serie löschen.',
};

export function RecurrenceScopeDialog({ mode, seriesLabel, onPick, onCancel }: RecurrenceScopeDialogProps) {
  const [pending, setPending] = React.useState<RecurrenceScope | null>(null);
  const overlayMouseDownRef = React.useRef(false);
  const busy = pending != null;

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      if (!busy) onCancel();
    };
    const resetOverlayPress = () => {
      overlayMouseDownRef.current = false;
    };
    window.addEventListener('keydown', onKey, true);
    document.addEventListener('mouseup', resetOverlayPress);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      document.removeEventListener('mouseup', resetOverlayPress);
    };
  }, [busy, onCancel]);

  const handlePick = async (scope: RecurrenceScope) => {
    if (busy) return;
    setPending(scope);
    try {
      await onPick(scope);
    } finally {
      setPending(null);
    }
  };

  const titleId = 'recurrence-scope-dialog-title';

  return createPortal(
    <div
      className={`fixed inset-0 ${UI_Z_CLASS.fullscreenModal} flex items-center justify-center bg-slate-900/45 p-4`}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-busy={busy}
      onMouseDown={(event) => {
        overlayMouseDownRef.current = event.target === event.currentTarget;
      }}
      onMouseUp={(event) => {
        if (busy) return;
        if (event.target === event.currentTarget && overlayMouseDownRef.current) onCancel();
      }}
    >
      <div
        className="relative w-full max-w-md overflow-hidden rounded-xl border border-app-border bg-app-surface shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 px-5 py-4">
          <h2 id={titleId} className="text-sm font-semibold text-app-text">
            {mode === 'delete' ? 'Serie löschen' : 'Serie speichern'}
          </h2>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400 hover:bg-app-hover hover:text-app-text-muted disabled:opacity-40"
            aria-label="Schließen"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="px-5 pb-4 text-sm leading-relaxed text-app-text-muted">{seriesLabel}</p>
        <div className="space-y-2 px-5">
          {OPTIONS.map((opt) => {
            const selected = pending === opt.scope;
            return (
              <button
                key={opt.scope}
                type="button"
                disabled={busy}
                onClick={() => void handlePick(opt.scope)}
                className={`flex w-full items-start justify-between gap-3 rounded-md border border-app-border px-3 py-2.5 text-left transition-colors ${
                  selected
                    ? 'border-pipedrive-blue/40 bg-app-surface-muted'
                    : busy
                      ? 'opacity-40'
                      : 'hover:bg-app-hover'
                }`}
              >
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-app-text">{opt.title}</span>
                  <span className="mt-0.5 block text-xs text-app-text-subtle">
                    {mode === 'delete' ? DELETE_HINTS[opt.scope] : opt.hint}
                  </span>
                </span>
                {selected ? (
                  <LoadingSpinner size="sm" className="mt-0.5 shrink-0" />
                ) : null}
              </button>
            );
          })}
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="inline-flex h-9 items-center justify-center rounded-md border border-app-border bg-app-surface px-3.5 text-sm font-medium text-app-text-muted transition-colors hover:bg-app-hover disabled:opacity-40"
          >
            Abbrechen
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
